/**
 * Retry wrapper around the onboarding completion handoff (LEV-1261 follow-up).
 *
 * IdeaChatInput hands the final OnboardingResult to its parent via onComplete.
 * For anonymous founders the parent pushes that result onto the server-side
 * onboarding session (the sole claim source) before redirecting to sign-up —
 * and on the Skip path that push is the ONLY place the enriched result exists,
 * so a failed push must surface a retry instead of navigating into a doomed
 * claim ("We lost your idea"). The parent signals the outcome by resolving
 * false; this hook tracks it so the UI can swap the building spinner for a
 * retry affordance. sessionStorage still caches the conversation for
 * reload/restore UX, but it is never used for project conversion.
 */
"use client";

import { useCallback, useRef, useState } from "react";
import type { OnboardingResult } from "@/lib/onboarding/schema";
import { captureClientError } from "@/lib/sentry/capture-client";

export type HandoffState = "idle" | "inFlight" | "failed";

export type OnCompleteHandler = (result: OnboardingResult) => void | Promise<boolean>;

export function useOnboardingHandoff(onComplete: OnCompleteHandler): {
  handoffState: HandoffState;
  begin: (result: OnboardingResult) => void;
  retry: () => void;
} {
  const [handoffState, setHandoffState] = useState<HandoffState>("idle");
  const lastResultRef = useRef<OnboardingResult | null>(null);

  const begin = useCallback(
    (result: OnboardingResult) => {
      lastResultRef.current = result;
      setHandoffState("inFlight");
      let outcome: void | Promise<boolean>;
      try {
        outcome = onComplete(result);
      } catch (err) {
        void captureClientError(err, {
          feature: "onboarding",
          errorType: "handoff-handler-threw",
          impact: "degraded",
        });
        setHandoffState("failed");
        return;
      }
      if (outcome && typeof outcome.then === "function") {
        outcome
          .then((ok) => {
            // void / true = navigation imminent — stay inFlight so the
            // building spinner persists until the page unmounts.
            if (ok === false) setHandoffState("failed");
          })
          .catch(() => setHandoffState("failed"));
      }
    },
    [onComplete],
  );

  const retry = useCallback(() => {
    if (!lastResultRef.current) return;
    begin(lastResultRef.current);
  }, [begin]);

  return { handoffState, begin, retry };
}
